import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  isChrome,
  isSafari,
  isEdge,
  isIOS,
  isAndroid,
} from 'react-device-detect';
import { IState } from 'store';
import { ILandingState } from 'store/modules/landing/types';
import {
  getLandingContentRequest,
  setFloatingBalloon,
} from 'store/modules/landing/actions';

import { IRecurrentContent } from 'models/recurrentDoubts';

import backgroundElement from 'assets/images/background-elements.png';
import device from 'assets/images/device.png';

import Button from 'shared/Button';
import Text from 'shared/Tipografia/Text';
import CardIcon from 'shared/Card/CardIcon';
import Link from 'shared/Tipografia/Link';
import Icon from 'shared/Icon';
import FloatingChatButton from 'shared/FloatingChatButton';
import { Align } from 'shared/Tipografia/Enum';
import { RecurrentDoubts } from 'shared/RecurrentDoubts';

import theme from 'styles/theme';

import ButtonSpinnerLoader from 'shared/Button/ButtonSpinnerLoader';
import {
  CallCenterSection,
  Telephone,
  CardsContent,
  ChatContext,
  ChatContent,
  Container,
  FeaturesSection,
  RecurrentDoubtsContent,
  Footer,
  Header,
  LeftSide,
  PrivacyAndTermsSection,
  RightSide,
  ManualSection,
  Wrapper,
} from './styles';

interface CardItem {
  title: string;
  description: string;
  iconName: string;
}

const Landing: React.FC = () => {
  const [itensOpened, setItensOpened] = React.useState<string[]>([]);
  const [subitensOpened, setSubitensOpened] = React.useState<string[]>([]);

  const { deferredPrompt } = useSelector<IState, ILandingState>(
    (state) => state.landing,
  );

  const { data, loading } = useSelector<IState, ILandingState>(
    (state) => state.landing,
  );

  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const cardItems: CardItem[] = [
    {
      title: 'Demonstrativos',
      description:
        'Fazer download e visualizar todos os pagamentos dos últimos 12 meses, inclusive de 13º salário, de forma detalhada.',
      iconName: 'paycheck',
    },
    {
      title: 'Benefícios',
      description:
        'Informações sobre benefícios, alterações e inclusões no plano de saúde etc.',
      iconName: 'benefits',
    },
    {
      title: 'Espelho de Ponto',
      description:
        'Fazer download e visualizar a folha de ponto dos últimos 12 meses para controlar e acompanhar seus horários e banco de horas.',
      iconName: 'timeSheet',
    },
    {
      title: 'Solicitações',
      description: 'Acompanhe suas solicitações e chamados pelo aplicativo.',
      iconName: 'solicitacoes',
    },
    {
      title: 'Central de Apoio ao Colaborador',
      description:
        'Acessar o chat da Central, seu canal direto com o RH para tirar dúvidas e fazer outras solicitações.',
      iconName: 'chatCircle',
    },
    {
      title: 'Comunicados',
      description: 'Leia os últimos comunicados e informativos da empresa.',
      iconName: 'announcements',
    },
  ];

  const recurrentDoubts: IRecurrentContent[] = [
    {
      id: '0',
      title: 'Como acesso o aplicativo?',
      content: `<p>Para acessar o novo RH Digital, insira o seu CPF.</p>
      <br>
      <p>Em seguida, você receberá um código por SMS no seu celular.</p>
      <p>Basta preencher com o código e pronto!</p>
      <br>
      <p>Aproveite o novo RH Digital e fique por dentro de tudo que acontece na Rede D’Or!</p>
      <br>
      <p>Veja como funciona:</p>`,
      contentVideo: 'https://www.youtube.com/watch?v=2XdL6Jy3Tns',
    },
    {
      id: '1',
      title: 'Como vejo meus Demonstrativos de Pagamento?',
      content: `<p>Acesse os Demonstrativos de Pagamento dos últimos <b>12 meses correntes</b>, incluindo os de 13° salário (primeira e segunda parcelas), como os disponibilizados pelo Portal RH. É possível baixar os documentos em PDF.</p>
      <br>
      <p>Veja como funciona:</p>`,
      contentVideo: 'https://www.youtube.com/watch?v=C0M4wi8SvfA',
    },
    {
      id: '2',
      title: 'Como acessar meu Espelho de Ponto?',
      content: `<p>Acesse seus Espelhos de Ponto dos últimos <b>12 meses correntes</b>, como os disponibilizados pelo Apdata. É possível baixar os documentos em PDF.<p>
      <br>
      <p>Veja como funciona:</p>`,
      contentVideo: 'https://www.youtube.com/watch?v=yW7fAMMulU0',
    },
  ];

  function goToAuth() {
    history.push({
      pathname: '/auth',
      state: { from: location.pathname },
    });
  }

  const handleInstallClick = () => {
    if (isIOS) {
      dispatch(setFloatingBalloon({ showFloatingBalloon: true }));
      return;
    }
    if (isAndroid && !isChrome && !isEdge) {
      history.push('/auth');
    }
    if (isIOS && !isSafari && !isChrome) {
      history.push('/auth');
    }
    // Show the install prompt
    deferredPrompt.prompt();
  };

  // Abrir manual do aplicativo
  const handleOpenManual = () => {
    const manualPdf = data[0]?.acf.manual_pdf;

    window.open(manualPdf);
  };

  useEffect(() => {
    dispatch(getLandingContentRequest());
  }, [dispatch]);

  return (
    <Container>
      <ChatContext>
        <Header>
          <div>
            <LeftSide>
              <Icon name="logoRhDigitalDefault" width="207" height="38" />

              <Text as="h1">
                Seja bem-vindo(a) ao seu novo <span>RH Digital.</span>
              </Text>

              <Text as="h2">
                A partir de agora você terá tudo de forma simples, onde você
                estiver, no seu celular.
              </Text>

              <Text as="h2">
                Com o aplicativo <span>RH Digital</span> você vai ficar por
                dentro das comunicações e informações do RH sobre serviços,
                pagamentos e benefícios, com facilidade e atendimento em
                qualquer lugar.
              </Text>

              <Button className="knowRhDigital" onClick={goToAuth}>
                Conheça o novo RH Digital
              </Button>

              <Button
                className="downloadRhDigital"
                onClick={handleInstallClick}
              >
                Instale o novo App RH Digital
              </Button>
            </LeftSide>

            <RightSide>
              <img
                src={backgroundElement}
                alt="Detalhes quadriculados de fundo"
                className="backgroundElement"
              />

              <img
                src={device}
                alt="Imagem de um smartphone exibindo elementos da tela inicial do RH Digital"
                className="deviceElement"
              />
            </RightSide>
          </div>
        </Header>

        <FeaturesSection>
          <Text as="h1">Mais fácil, mais prático, na palma da mão</Text>

          <CardsContent>
            {cardItems.map((item) => (
              <CardIcon
                key={item.title}
                title={item.title}
                description={item.description}
                iconName={item.iconName}
              />
            ))}
          </CardsContent>

          <Button className="downloadRhDigital" onClick={handleInstallClick}>
            Instale o novo App RH Digital
          </Button>
        </FeaturesSection>

        <ChatContent>
          <FloatingChatButton />
        </ChatContent>

        <RecurrentDoubtsContent>
          <div className="innerContent">
            <Text as="h2" align={Align.CENTER} color={theme.colors.black}>
              Dúvidas Recorrentes
            </Text>

            <RecurrentDoubts
              itensOpened={itensOpened}
              setItensOpened={setItensOpened}
              subitensOpened={subitensOpened}
              setSubitensOpened={setSubitensOpened}
              recurrentDoubtsObj={recurrentDoubts}
            />
          </div>
        </RecurrentDoubtsContent>
      </ChatContext>

      <Footer>
        <ManualSection>
          <Wrapper>
            <Text as="h2">Quer saber mais?</Text>

            <Text as="h3">Acesse o manual do app e saiba como funciona</Text>
          </Wrapper>

          <ButtonSpinnerLoader
            type="button"
            id="requestAnnouncements"
            className="downloadRhDigital"
            onClick={handleOpenManual}
            isLoading={loading}
          >
            Veja o manual do aplicativo
          </ButtonSpinnerLoader>
        </ManualSection>

        <CallCenterSection>
          <Text as="h1">
            Em caso de dúvidas, entre em contato com a Central de Apoio ao
            Colaborador.
          </Text>
          <Telephone>
            <Icon name="telephone" width="28" height="28" />
            <div>
              <span>Central de apoio ao colaborador</span>
              <h3>0800 334 5000</h3>
            </div>
          </Telephone>
        </CallCenterSection>

        <PrivacyAndTermsSection>
          <div>
            <Text as="p">@2020 REDE D’OR</Text>
            <Text as="p">TODOS OS DIREITOS RESERVADOS.</Text>
          </div>

          <div>
            <Link
              href="https://dpo.privacytools.com.br/policy-view/NjVGErqo1/1/policy/pt_BR?s=1680026544140"
              target="_blank"
              textDecoration="none"
            >
              Aviso de privacidade
            </Link>
            <Link
              href="https://dpo.privacytools.com.br/policy-view/mvpVEkO69/3/termos-de-uso---rh-digital/pt_BR?s=1680200213834"
              target="_blank"
              textDecoration="none"
            >
              Termos de uso
            </Link>
          </div>
        </PrivacyAndTermsSection>
      </Footer>
    </Container>
  );
};

export default Landing;
